import {Component, NgZone, OnInit, OnDestroy} from '@angular/core';
import {JsBridgeService} from "./services/js-bridge.service";
import {LocalStorageService} from "./services/local-storage.service";
import {GlobalService} from "./services/global.service";
import {PushContent} from "./shared/interfaces/pushContent";
import {NativeFileUpload} from "./shared/interfaces/nativeFileUpload";
import {Router, NavigationEnd, ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {PushContentService} from "./services/push-content.service";
import {PushTokenService} from "./services/push-token.service";
import {DocumentiComuneGateService} from "./services/documenti-comune/documenti-comune-gate.service";
import {IonApp, IonRouterOutlet, ToastController} from "@ionic/angular/standalone";
import {filter, map, Subscription} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: true,
    imports: [IonApp, IonRouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {

  routeAnnouncement = '';
  private routeSub?: Subscription;

  constructor(
    private _ngZone: NgZone,
    protected localStorageService: LocalStorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private pushContentService: PushContentService,
    private pushTokenService: PushTokenService,
    private jsBridgeService: JsBridgeService,
    private globalService: GlobalService,
    private toastController: ToastController,
    private transloco: TranslocoService,
    private documentiComuneGate: DocumentiComuneGateService,
  ) {
    window.AppComponentRef = {
      zone: this._ngZone,
      component: this,
    };
  }

  ngOnInit(): void {
    window.onPushReceived = (pushContent) => this.onPushReceived(pushContent);
    window.onDeviceIdReceived = (deviceId) => this.onDeviceIdReceived(deviceId);
    window.onPushTokenReceived = (token) => this.onPushTokenReceived(token);
    window.setAsMobilePlatform = () => this.setAsMobilePlatform();
    window.setVersionAndPlatform = (vp) => this.setVersionAndPlatform(vp);
    window.openUrlFromUniversalLink = (vp) => this.openUrlFromUniversalLink(vp);
    window.onQrCodeValueReceived = (value) => this.onQrCodeValueReceived(value);
    window.onFileReceived = (data) => this.onFileReceived(data);
    window.onFileUploadError = (error) => this.onFileUploadError(error);

    this.localStorageService.isMobilePlatform = this.isWebView;
    this.markNativeShellForSafeArea();

    // Modale dei documenti del Comune: parte da sola quando ci sono sessione e Comune.
    this.documentiComuneGate.avvia();

    this.routeSub = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.snapshot.title;
      })
    ).subscribe(pageTitle => {
      const title = pageTitle ? `${pageTitle} - CivesHub` : 'CivesHub';
      this.titleService.setTitle(title);
      this.routeAnnouncement = pageTitle ? `Pagina: ${pageTitle}` : '';
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  openUrlFromUniversalLink(url: string): void {
    console.log('[openUrlFromUniversalLink] raw url:', url);
    console.log('[openUrlFromUniversalLink] IsInAngularZone:', NgZone.isInAngularZone());

    if (!url) {
      console.warn('[openUrlFromUniversalLink] invalid url');
      return;
    }

    const ref = window.AppComponentRef;
    if (!ref) {
      console.warn('[openUrlFromUniversalLink] AppComponentRef is undefined');
      return;
    }

    ref.zone.run(() => {
      try {
        const normalizedUrl = this.normalizeUniversalLink(url);
        console.log('[openUrlFromUniversalLink] navigating to:', normalizedUrl);

        this.router.navigateByUrl(normalizedUrl);
      } catch (err) {
        console.error('[openUrlFromUniversalLink] navigation error', err);
        // Universal-link non navigabile: fallback sicuro alla home invece di lasciare l'utente bloccato.
        this.router.navigateByUrl('/pages/home');
      }
    });
  }
  private normalizeUniversalLink(url: string): string {
    if (url.startsWith('/')) {
      return url;
    }
    try {
      const parsed = new URL(url);

      return parsed.pathname + parsed.search + parsed.hash;
    } catch {
      return '/' + url;
    }
  }


  onDeviceIdReceived(deviceId: string) {
    console.log('[onDeviceIdReceived] id:', deviceId);
    console.log('[onDeviceIdReceived] IsInAngularZone:', NgZone.isInAngularZone());

    window.AppComponentRef?.zone.run(() => {
      console.log('[onDeviceIdReceived] AppComponentRef run invoked.');
      window.AppComponentRef?.component.localStorageService.setDeviceId(deviceId);
    });
  }

  /**
   * Push token FCM in arrivo dal guscio nativo. Il valore non viene loggato: identifica un
   * dispositivo e vale come indirizzo di recapito.
   *
   * <p>Il rientro in NgZone è necessario perché la chiamata arriva da fuori Angular
   * (`evaluateJavascript`/`callAsyncJavaScript`): senza, la richiesta HTTP partirebbe comunque ma
   * fuori dalla zona, e gli aggiornamenti di stato conseguenti non innescherebbero change detection.
   */
  onPushTokenReceived(token: string) {
    console.log('[onPushTokenReceived] token ricevuto:', !!token);

    const ref = window.AppComponentRef;
    if (!ref) {
      console.warn('[onPushTokenReceived] AppComponentRef is undefined');
      return;
    }

    ref.zone.run(() => this.pushTokenService.onTokenNativoRicevuto(token));
  }

  setAsMobilePlatform() {
    console.log('[setAsMobilePlatform]');

    const ref = window.AppComponentRef;
    if (!ref) {
      console.warn('[setAsMobilePlatform] AppComponentRef is undefined');
      return;
    }

    ref.zone.run(() => {
      console.log('[setAsMobilePlatform] AppComponentRef run invoked.');
      ref.component.localStorageService.isMobilePlatform = true;
    });
  }



  setVersionAndPlatform(versionAndPlatform: { version: string, platform: string }) {
    console.log('[setVersionAndPlatform]', JSON.stringify(versionAndPlatform));
    window.AppComponentRef?.zone.run(() => {
      this.globalService.appVersion = versionAndPlatform.version;
    });
  }

  onQrCodeValueReceived(value: string) {
    console.log('[onQrCodeValueReceived] value:', value);
    window.AppComponentRef?.zone.run(() => {
      this.globalService.nextScannerValue(value);
    });
  }

  onFileReceived(data: NativeFileUpload) {
    console.log('[onFileReceived]', data.fileName, data.fileSize);
    window.AppComponentRef?.zone.run(async () => {
      try {
        const binaryString = atob(data.file);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.fileType });
        const file = new File([blob], data.fileName, { type: data.fileType });
        this.globalService.nextNativeFileReceived(file);
      } catch (e) {
        console.error('[onFileReceived] conversion error', e);
        const toast = await this.toastController.create({
          message: this.transloco.translate('common.toast.file_invalid'),
          duration: 3000,
          position: 'bottom',
          color: 'danger',
        });
        await toast.present();
      }
    });
  }

  async onFileUploadError(error: string) {
    console.warn('[onFileUploadError]', error);
    window.AppComponentRef?.zone.run(async () => {
      const toast = await this.toastController.create({
        message: error,
        duration: 3000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    });
  }

  /*
  MARK: FUNZIONI DI LETTURA SU PONTE JS
  */
  /**
   * Payload di una notifica push in arrivo dal guscio nativo.
   *
   * <p>`PushContentService` decide se aprire la modale subito o mettere il contenuto in attesa: se
   * non c'è una sessione attiva la modale comparirebbe sopra la pagina di login e il contenuto
   * andrebbe perso alla chiusura. Qui non si discrimina nulla.
   *
   * <p><b>`clearPushNotification()` resta immediato, e non spostato al consumo del contenuto.</b>
   * Serve a pulire la tray e lo stato pendente lato nativo, che è la coda "prima che la SPA sia
   * avviata": il nativo la rigioca a OGNI `appInitEnd`. Se lo si chiamasse solo alla presentazione
   * della modale, un utente che non completa il login lascerebbe lo stato nativo sporco a tempo
   * indefinito, e ogni riavvio riconsegnerebbe lo stesso payload — che ora, essendo persistito
   * lato web, produrrebbe un doppione. È proprio la persistenza web dell'attesa a rendere sicura
   * la pulizia immediata del lato nativo: la titolarità del contenuto passa alla web app.
   */
  onPushReceived(pushContent: PushContent) {
    console.log('[onPushReceived] pushContent:', pushContent);
    console.log('[onPushReceived] IsInAngularZone:', NgZone.isInAngularZone());

    window.AppComponentRef?.zone.run(() => {
      console.log('[onPushReceived] AppComponentRef run invoked.');

      window.AppComponentRef?.component.pushContentService.gestisciNotifichePush(pushContent);
      window.AppComponentRef?.component.jsBridgeService.clearPushNotification();
    });
  }



  /**
   * Marca `<html>` con `native-shell` quando la web app gira dentro un guscio nativo, così il CSS
   * può azzerare `--ion-safe-area-top` (vedi `global.scss`, MARK SAFE-AREA).
   *
   * Il problema che risolve: l'inset della status bar veniva applicato DUE volte, e si vedeva come
   * una fascia scura fra la barra di sistema e l'header.
   *   1. Il guscio nativo lo consuma già: su Android `WebViewActivity.applyWindowInsets` mette gli
   *      inset come padding sulla radice del layout; su iOS `WebViewController` ancora la WebView a
   *      `safeAreaLayoutGuide`. In entrambi i casi il viewport INIZIA sotto la status bar.
   *   2. Ionic aggiunge `padding-top: var(--ion-safe-area-top)` sul primo `ion-toolbar` dentro
   *      `ion-header` — pensato per il notch di Safari, dove nessuno ha già compensato l'inset. Non
   *      dipende dal `mode: 'ios'` forzato in main.ts: la regola è identica in `header.ios.css` e
   *      `header.md.css`.
   *
   * Perché serve una classe e non un override CSS secco: nel browser (dev server, uso da desktop o
   * da Safari mobile senza guscio) nessuno consuma l'inset, quindi lì `--ion-safe-area-top` è
   * legittimo e azzerarlo farebbe finire l'header sotto il notch. La distinzione non è "Android vs
   * iOS", è "dentro un guscio vs no".
   *
   * Il test è sulla presenza del ponte (`JSInterface` su Android, `webkit.messageHandlers` su iOS) e
   * NON su `isWebView`: quel getter legge `platform` da `localStorage`, che al primo avvio non è
   * ancora scritto (arriva da `setAsMobilePlatform`, dopo `appInitEnd`) e produrrebbe un salto
   * visibile dell'header a inizializzazione avvenuta.
   */
  private markNativeShellForSafeArea(): void {
    const w = window as any;
    const inNativeShell = !!w.JSInterface || !!w.webkit?.messageHandlers?.appInitEnd;
    if (inNativeShell) {
      document.documentElement.classList.add('native-shell');
    }
  }

  get isWebView(): boolean {
    const platformType = this.localStorageService.getPlatform();
    console.log(platformType, 'platformType')
    const isIosWebView = platformType?.toLowerCase() === 'IOS'.toLowerCase();
    const isAndroidWebView = platformType?.toLowerCase() === 'ANDROID'.toLowerCase();
    const isHuaweiWebView = platformType?.toLowerCase() === 'HUAWEI'.toLowerCase();

    return isAndroidWebView || isIosWebView || isHuaweiWebView;
  }

}
